/**
 * Utilities for the legal page renderer: slug from heading text, TOC extraction
 * from raw markdown, plain-text extraction from React nodes.
 *
 * Anchor slugs strip leading section numbers ("1. Definitions" → "definitions")
 * for cleaner shareable URLs while the TOC label keeps the number for scanning.
 */

import type { ReactNode } from 'react';

export type TocEntry = {
  id: string;       // anchor slug for href + heading id
  label: string;    // displayed label (keeps leading "N." prefix)
  level: 2 | 3;     // H2 or H3
};

const STRIP_NUMBER_PREFIX = /^\d+(\.\d+)?\.\s+/;

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(STRIP_NUMBER_PREFIX, '')        // drop "1. " / "2.1 "
    .replace(/[^a-z0-9\s-]/g, '')            // remove punctuation
    .replace(/\s+/g, '-')                    // spaces → dashes
    .replace(/-+/g, '-')                     // collapse runs
    .replace(/^-|-$/g, '');                  // trim
}

/**
 * Extract H2 (and optional H3) headings from raw markdown, in document order.
 * Skips fenced code blocks so `## inside code` isn't picked up.
 */
export function extractToc(markdown: string, includeH3 = false): TocEntry[] {
  const entries: TocEntry[] = [];
  const lines = markdown.split('\n');
  let inFence = false;

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();

    if (line.startsWith('```')) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;

    const h2Match = /^##\s+(.+?)\s*$/.exec(line);
    if (h2Match) {
      const label = h2Match[1].trim();
      entries.push({ id: slugify(label), label, level: 2 });
      continue;
    }

    if (includeH3) {
      const h3Match = /^###\s+(.+?)\s*$/.exec(line);
      if (h3Match) {
        const label = h3Match[1].trim();
        entries.push({ id: slugify(label), label, level: 3 });
      }
    }
  }

  return entries;
}

/**
 * Extract plain text from a React node tree — used to derive anchor IDs from
 * markdown heading children that may contain inline formatting.
 */
/**
 * Strip the metadata prologue from a legal-document markdown source.
 *
 * Every legal doc starts with `# Title` followed by a metadata block
 * (Effective date / Last updated / Operator / Contact) ending at the first
 * horizontal rule (`---`). The page chrome is the canonical place for
 * metadata, so this helper removes the prologue before rendering to avoid
 * duplication in the body.
 *
 * The H1 and the metadata block before the first `---` are dropped; the
 * rest of the document (starting after the rule) is returned. If no `---`
 * is present the source is returned unchanged.
 */
export function stripPrologue(markdown: string): string {
  const ruleIndex = markdown.search(/^---\s*$/m);
  if (ruleIndex === -1) return markdown;
  // Slice off everything up to and including the rule line + its newline
  const after = markdown.slice(ruleIndex);
  const newlineAfterRule = after.indexOf('\n');
  if (newlineAfterRule === -1) return '';
  return after.slice(newlineAfterRule + 1).trimStart();
}

export function extractText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (typeof node === 'object' && 'props' in node) {
    const props = (node as { props?: { children?: ReactNode } }).props;
    return extractText(props?.children);
  }
  return '';
}
